import { useEffect, useState } from "react";
import { Button } from "../common/Button";
import { Field } from "../common/Field";

/**
 * Who, and between when (#314).
 *
 * The everyday question is "what did my helper do today", and the same
 * controls answer the uncomfortable one -- "what did this person change
 * last week" -- without the app needing an incident mode. Most worries
 * end here: knowing what happened is what people actually want first,
 * and it is the half an owner can answer themselves now that reverting
 * is administrators-only (#315).
 *
 * PRESETS RATHER THAN TWO DATE BOXES. "Today" and "this week" are what
 * somebody actually wants, and a pair of empty date fields makes the
 * common case the most work. The exact range stays available for the
 * rare case that needs it.
 *
 * Renders the person filter only when there IS more than one person. On
 * a solo account "filter by: me" is furniture, and the same reasoning
 * hides the account switcher (#310).
 */
const PRESETS = [
  { key: "today", label: "Today", days: 0 },
  { key: "week", label: "Last 7 days", days: 7 },
  { key: "month", label: "Last 30 days", days: 30 },
  { key: "all", label: "Everything", days: null },
];

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export function ActivityFilters({ people = [], onChange, busy = false }) {
  const [preset, setPreset] = useState("week");
  const [userId, setUserId] = useState("");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  // Fire on mount too, so the card opens showing the default window
  // rather than everything and then narrowing under the reader.
  useEffect(() => {
    const chosen = PRESETS.find((p) => p.key === preset);
    onChange?.({
      userId: userId || undefined,
      since: preset === "custom" ? customFrom || undefined
             : chosen?.days === null ? undefined
             : isoDaysAgo(chosen.days),
      until: preset === "custom" ? customTo || undefined : undefined,
    });
    // onChange is recreated each render by most callers; depending on it
    // would refetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, userId, customFrom, customTo]);

  return (
    <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", alignItems: "flex-end",
                  marginBottom: "var(--space-3)" }}>
      <div style={{ display: "flex", gap: "var(--space-1)", flexWrap: "wrap" }}>
        {PRESETS.map((p) => (
          <Button
            key={p.key}
            size="sm"
            variant={preset === p.key ? "primary" : "secondary"}
            onClick={() => setPreset(p.key)}
            disabled={busy}
          >
            {p.label}
          </Button>
        ))}
        <Button
          size="sm"
          variant={preset === "custom" ? "primary" : "secondary"}
          onClick={() => setPreset("custom")}
          disabled={busy}
        >
          Dates…
        </Button>
      </div>

      {preset === "custom" && (
        <>
          <Field label="From">
            {(id) => (
              <input id={id} type="date" value={customFrom} className="touch-target"
                     onChange={(e) => setCustomFrom(e.target.value)} />
            )}
          </Field>
          <Field label="To" hint="Includes the whole day.">
            {(id) => (
              <input id={id} type="date" value={customTo} className="touch-target"
                     onChange={(e) => setCustomTo(e.target.value)} />
            )}
          </Field>
        </>
      )}

      {people.length > 0 && (
        <Field label="Who">
          {(id) => (
            <select id={id} value={userId} className="touch-target"
                    onChange={(e) => setUserId(e.target.value)}>
              <option value="">Everyone</option>
              {people.map((p) => (
                <option key={p.user_id} value={p.user_id}>{p.label}</option>
              ))}
            </select>
          )}
        </Field>
      )}
    </div>
  );
}
